import { useEffect, useState, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COPY = {
  success: { label: "Verified", cls: "text-green-700 bg-green-50 border-green-200" },
  pending_review: { label: "In review", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  active: { label: "In progress", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  failed: { label: "Failed — please retry", cls: "text-red-700 bg-red-50 border-red-200" },
  expired: { label: "Expired — please retry", cls: "text-muted-foreground bg-muted border-border" },
  not_started: { label: "Not verified", cls: "text-muted-foreground bg-muted border-border" },
};

export function IdentityVerification({ onVerified }) {
  const [status, setStatus] = useState("not_started");
  const [verified, setVerified] = useState(false);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async (announce) => {
    try {
      const { data } = await api.get("/identity_verification/status");
      setStatus(data.status || "not_started");
      setVerified(!!data.identity_verified);
      if (announce) {
        if (data.identity_verified) { toast.success("Identity verified"); onVerified && onVerified(); }
        else toast.info(`Verification ${data.status || "submitted"} — your badge appears once approved.`);
      }
    } catch { /* not configured or no session yet */ }
  }, [onVerified]);

  useEffect(() => { refresh(false); }, [refresh]);

  // onSuccess means the Link flow finished, NOT that identity passed — confirm with backend.
  const onSuccess = useCallback(() => { setToken(null); refresh(true); }, [refresh]);
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit: () => setToken(null) });
  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/identity_verification/create");
      setStatus(data.status || "active");
      setToken(data.link_token);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to start verification");
    } finally { setBusy(false); }
  };

  const meta = STATUS_COPY[status] || STATUS_COPY.not_started;

  return (
    <div className="mt-6 bg-white border border-border rounded-sm p-6" data-testid="identity-verification">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-medium flex items-center gap-2">
            <ShieldCheck className={`w-5 h-5 ${verified ? "text-green-600" : "text-muted-foreground"}`} />
            Identity verification <span className="text-xs font-normal text-muted-foreground">(optional)</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Verify your identity to earn a green Verified shield on your profile and services. It builds trust and helps customers book with confidence.
          </p>
          <span className={`inline-block mt-3 text-xs font-semibold border rounded-sm px-2 py-1 ${meta.cls}`} data-testid="idv-status-badge">{meta.label}</span>
        </div>
        {!verified && (
          <Button onClick={start} disabled={busy || (!!token && !ready)} className="rounded-sm" data-testid="verify-identity-btn">
            {busy
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Starting…</>
              : <><ShieldCheck className="w-4 h-4 mr-1.5" />{status === "not_started" ? "Verify identity" : "Continue verification"}</>}
          </Button>
        )}
      </div>
    </div>
  );
}
